/**
 * flush.ts — outbox flush orchestrator.
 *
 * Triggered by the 'online' event, app start, or a manual button.
 * All triggers funnel into one run() call.
 *
 * Design decisions:
 *   ADR-4: foreground online-event flush (not SW Background Sync).
 *   ADR-8: single-flight in-memory lock for one-tab correctness.
 */

import { toast } from "sonner";
import { httpPost } from "@/shared/lib/http-client.js";
import * as outbox from "./outbox.js";
import { drainBlobs, getBlobsForReport } from "./blob-queue.js";
import { getReport, putReport, getChildReports } from "./reports-local.js";
import type { OutboxItem, SyncResultItem } from "../types.js";

// ─── flush summary ──────────────────────────────────────────────────────────

/** Outcome of a flush run, so callers (e.g. manual "Sincronizar") can report honestly. */
export interface FlushSummary {
  /** created or updated on the server this run. */
  synced: number;
  /** server already had a newer version (reconciled locally, no longer pending). */
  skipped: number;
  /** server returned an error or the network failed — still queued for retry. */
  errored: number;
  /** held back because their blobs (e.g. firma) could not be uploaded — still queued. */
  blobBlocked: number;
}

// ─── flush subscribers ────────────────────────────────────────────────────────

type FlushSubscriber = (summary: FlushSummary) => void;
const flushSubscribers = new Set<FlushSubscriber>();

/**
 * Subscribe to flush completions. The callback fires after every REAL flush run
 * (i.e., when `run()` was not short-circuited by the single-flight lock).
 *
 * Returns an unsubscribe function — call it on component unmount to avoid leaks.
 *
 * Used by TechnicianPanel to re-read the outbox summary after any auto-flush
 * (cold-start, reconnect, or visibility change), ensuring SyncStatusPanel always
 * reflects the correct count after the queue drains — even when the flush was
 * triggered by AuthGuard's useFlushOnReconnect rather than by TechnicianPanel
 * itself (in which case TechnicianPanel never bumps its own reloadKey).
 */
export function subscribeToFlush(fn: FlushSubscriber): () => void {
  flushSubscribers.add(fn);
  return () => flushSubscribers.delete(fn);
}

// ─── single-flight lock ───────────────────────────────────────────────────────

// ponytail: foreground-only single-flight; ceiling = multi-tab; upgrade = navigator.locks
let flushing = false;

// ─── session-expired idempotency flag (Fase 3) ────────────────────────────────

/**
 * Guards the session-expired toast against repeated fires on every reconnect
 * event. Set to true on the first "session-expired" error; reset automatically
 * when the module is reloaded (re-login typically causes a page reload or a
 * fresh auth state that makes refreshHandler succeed again).
 *
 * ponytail: module-level flag; ceiling = SPA re-login without page reload —
 * the flag stays true but won't trigger incorrectly (refreshHandler succeeds
 * after re-login so the "session-expired" path is never reached again).
 */
let sessionExpiredNotified = false;

// ─── refresh handler (wired in S4-T1) ────────────────────────────────────────

let refreshHandler: (() => Promise<void>) | null = null;

/** Register the auth refresh handler. Called during app bootstrap (S4-T1 wires this). */
export function setFlushRefreshHandler(fn: () => Promise<void>): void {
  refreshHandler = fn;
}

// ─── blob placeholder injection ───────────────────────────────────────────────

/**
 * Replace `idb-blob:${ownerLocalId}:${fieldPath}` placeholders in a payload
 * object with their resolved R2 publicUrls.
 *
 * Walks the payload recursively and replaces any string value that starts with
 * `idb-blob:` with the matching remoteUrl from the uploaded blob records.
 *
 * ponytail: recursive JSON walk covers all nesting depths; ceiling = circular
 * references (not possible with plain report payloads).
 */
export function injectBlobUrls(
  payload: unknown,
  urlMap: Map<string, string>, // "idb-blob:ownerId:fieldPath" → publicUrl
): unknown {
  if (typeof payload === "string") {
    return urlMap.get(payload) ?? payload;
  }
  if (Array.isArray(payload)) {
    return payload.map((item) => injectBlobUrls(item, urlMap));
  }
  if (payload !== null && typeof payload === "object") {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(payload as Record<string, unknown>)) {
      result[k] = injectBlobUrls(v, urlMap);
    }
    return result;
  }
  return payload;
}

/**
 * Defensive recursive scan: true if ANY string value in the payload still
 * starts with `idb-blob:`. Used by resolvePayloadBlobs as a post-injection
 * assertion — a "resolved" payload that still contains placeholders must never
 * reach the server (it would render blank photos/firmas).
 *
 * ponytail: same JSON walk as injectBlobUrls; ceiling = circular references
 * (not possible with plain report payloads).
 */
export function containsBlobPlaceholders(payload: unknown): boolean {
  if (typeof payload === "string") {
    return payload.startsWith("idb-blob:");
  }
  if (Array.isArray(payload)) {
    return payload.some((item) => containsBlobPlaceholders(item));
  }
  if (payload !== null && typeof payload === "object") {
    return Object.values(payload as Record<string, unknown>).some((v) =>
      containsBlobPlaceholders(v),
    );
  }
  return false;
}

/**
 * Build the placeholder→publicUrl map for ONE report's uploaded blobs:
 * "idb-blob:ownerId:fieldPath" → publicUrl. Used by flush (all eligible items)
 * and by resolvePayloadBlobs (online finalize drains before the PATCH).
 */
export async function getUploadedBlobUrlMap(ownerLocalId: string): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>();
  const uploaded = await getBlobsForReport(ownerLocalId);
  for (const b of uploaded) {
    if (b.uploadState === "uploaded" && b.remoteUrl) {
      urlMap.set(`idb-blob:${b.ownerLocalId}:${b.fieldPath}`, b.remoteUrl);
    }
  }
  return urlMap;
}

/**
 * Resolve an item's parentId to the parent's server id when the parent is a
 * local report that has ALREADY synced.
 *
 * A continuation (IS→AC, or IS→IS) created offline carries parentId = the parent's
 * LOCAL id. When the parent syncs in the SAME run, reconcile() rewrites the child's
 * outbox parentId to the real serverId. But when the parent synced in an EARLIER
 * session, the parent is no longer pending, so the child is treated as a root and
 * would be sent with the raw local id → the server rejects it with a foreign-key
 * violation ("informes_parent_id_fkey"). Rewriting it here closes that gap.
 *
 * - parentId already a serverId (or absent) → getReport(parentId) is undefined → unchanged.
 * - parentId local + parent has serverId    → rewritten to the serverId.
 * - parentId local + parent still unsynced   → unchanged (reconcile handles it in-batch).
 */
async function resolveParentId(parentId: string | undefined): Promise<string | undefined> {
  if (!parentId) return parentId;
  const parent = await getReport(parentId);
  return parent?.serverId ?? parentId;
}

// ─── max batch size (NFR-4, spec §Batched and Ordered Flush) ─────────────────

const BATCH_SIZE = 200;

// ─── topological sort ─────────────────────────────────────────────────────────

/**
 * Sort outbox items parent-before-child.
 *
 * Items without a parentId in the SAME pending set go first.
 * Items whose parent IS in the pending set are ordered after it.
 * Preserves original order for items at the same depth.
 *
 * This ensures an offline IS→AC chain sends IS first, then AC
 * with the rewritten serverId parentId (done in reconcile).
 */
export function topoSort(items: OutboxItem[]): OutboxItem[] {
  const localIds = new Set(items.map((i) => i.localId));
  // Separate roots (no pending parent) from dependents.
  const roots = items.filter((i) => !i.parentId || !localIds.has(i.parentId));
  const children = items.filter((i) => i.parentId && localIds.has(i.parentId));

  // Build a simple one-level map: parentLocalId → children.
  // Deeper chains (IS→AC→...) are covered by iterative expansion below.
  const byParent = new Map<string, OutboxItem[]>();
  for (const child of children) {
    const p = child.parentId!;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p)!.push(child);
  }

  // BFS/topological order: expand roots, then append their children in order.
  const sorted: OutboxItem[] = [];
  const queue = [...roots];
  while (queue.length > 0) {
    const item = queue.shift()!;
    sorted.push(item);
    const deps = byParent.get(item.localId) ?? [];
    queue.push(...deps);
  }

  // Any items that didn't make it in (shouldn't happen with a proper set) go last.
  const inSorted = new Set(sorted.map((i) => i.localId));
  for (const item of items) {
    if (!inSorted.has(item.localId)) sorted.push(item);
  }

  return sorted;
}

// ─── reconcile ────────────────────────────────────────────────────────────────

/**
 * Process /sync results and update local state.
 *
 * created/updated → persist serverId+numero, mark sincronizado, remove from outbox.
 *                    Rewrite child parentId to new serverId for the next batch.
 * skipped         → remove from outbox, queue conflict toast (batched per run).
 * error           → mark error in outbox (stays for next flush).
 *
 * Returns the set of localIds whose parent errored (children must not be sent).
 */
async function reconcile(
  results: SyncResultItem[],
  skippedBucket: string[],
  summary: FlushSummary,
): Promise<void> {
  for (const result of results) {
    if (result.status === "created" || result.status === "updated") {
      summary.synced += 1;
      // Persist serverId + numero + estado sincronizado to the local report record.
      const existing = await getReport(result.localId);
      if (existing && result.serverId) {
        await putReport(
          {
            ...existing,
            serverId: result.serverId,
            numero: result.numero,
            estado: "sincronizado",
          },
          existing.updatedAt, // preserve the edit-time timestamp
        );

        // Rewrite children: any child whose parentLocalId points at this localId
        // should now use the real serverId as parentId for the next /sync call.
        const children = await getChildReports(result.localId);
        for (const child of children) {
          // Update the outbox item's parentId in-place by re-enqueueing with new parentId.
          // Since outbox.enqueue is last-write-wins and resets attempts, we patch the
          // outbox item directly via the DB to avoid disrupting syncState.
          const childOutboxItem = await import("../stores/db.js").then(async (m) => {
            const db = await m.getDb();
            return db.get("outbox", child.localId) as Promise<
              import("../types.js").OutboxItem | undefined
            >;
          });
          if (childOutboxItem) {
            await import("../stores/db.js").then(async (m) => {
              const db = await m.getDb();
              await db.put("outbox", {
                ...childOutboxItem,
                parentId: result.serverId,
              });
            });
          }
        }
      }
      await outbox.remove(result.localId);
    } else if (result.status === "skipped") {
      // Conflict: server version is newer — the server ALREADY HAS this report.
      // Reconcile the local record to sincronizado so it doesn't sit "Pendiente"
      // (en_cola) forever after being removed from the outbox. Without this, a
      // report that hit the skipped path would be stuck en_cola with no way to
      // retry (it's gone from the outbox).
      const existing = await getReport(result.localId);
      if (existing) {
        await putReport(
          {
            ...existing,
            serverId: result.serverId ?? existing.serverId,
            numero: result.numero ?? existing.numero,
            estado: "sincronizado",
          },
          existing.updatedAt,
        );
      }
      await outbox.remove(result.localId);
      skippedBucket.push(result.localId);
      summary.skipped += 1;
    } else if (result.status === "error") {
      await outbox.markError(result.localId, result.error ?? "unknown error");
      summary.errored += 1;
    }
  }
}

// ─── main orchestrator ────────────────────────────────────────────────────────

/**
 * run(trigger) — flush the outbox.
 *
 * Steps:
 *   A. drainBlobs() — upload queued blobs first (stub in S2, real in S3).
 *   B. Collect pending outbox items, topological sort, chunk ≤200.
 *   C. POST /sync per chunk, reconcile results.
 */
export async function run(
  trigger: "online" | "start" | "manual" | "visible",
): Promise<FlushSummary> {
  const summary: FlushSummary = { synced: 0, skipped: 0, errored: 0, blobBlocked: 0 };

  // Single-flight guard.
  if (flushing) return summary;
  flushing = true;

  try {
    // Force token refresh before any network call (wired in S4-T1).
    // Fase 3: catch "session-expired" (online + refresh token expired after a long
    // offline shift). Surface a clear toast, preserve the outbox untouched, and
    // return an empty summary so callers do not crash. The toast is idempotent —
    // shown at most once per module lifetime (sessionExpiredNotified flag).
    if (refreshHandler) {
      try {
        await refreshHandler();
      } catch (err) {
        if (err instanceof Error && err.message === "session-expired") {
          if (!sessionExpiredNotified) {
            sessionExpiredNotified = true;
            toast.error("Tu sesión expiró. Vuelve a iniciar sesión para sincronizar tus cambios.");
          }
          return summary; // outbox untouched — no data loss
        }
        throw err; // unexpected refresh error — propagate
      }
    }

    // Step A: drain blobs per pending report (S3-T6 — real blob upload).
    // For each pending item, upload its blobs to R2. If any blob fails, that
    // report is excluded from /sync this cycle (per-report atomicity, spec §Blob-to-R2).
    const pendingForBlobs = await outbox.getPending();
    const blobEligible = new Set<string>(); // localIds whose blobs are all uploaded
    for (const item of pendingForBlobs) {
      const drained = await drainBlobs(item.localId);
      if (drained) blobEligible.add(item.localId);
    }

    // Build URL map for placeholder injection: "idb-blob:ownerId:fieldPath" → publicUrl.
    const blobUrlMap = new Map<string, string>();
    for (const item of pendingForBlobs) {
      if (!blobEligible.has(item.localId)) continue;
      const itemMap = await getUploadedBlobUrlMap(item.localId);
      for (const [k, v] of itemMap) blobUrlMap.set(k, v);
    }

    // Step B: collect + sort + chunk. Only include blob-eligible items.
    const allPending = await outbox.getPending();
    const pending = allPending.filter((i) => blobEligible.has(i.localId));
    // Reports held back this run because their blobs (e.g. firma) failed to upload.
    summary.blobBlocked = allPending.length - pending.length;
    if (pending.length === 0) return summary;

    // Mark as inflight before sending (crash recovery: resetInflight on next start).
    await outbox.markInflight(pending.map((i) => i.localId));

    const sorted = topoSort(pending);

    // Track which localIds had errors — their children are held back.
    const erroredParents = new Set<string>();
    // Collect skipped IDs per flush run for batched toast.
    const skippedIds: string[] = [];
    // Process in topological order. Group into sub-batches of ≤200.
    // Within each sub-batch, send parents-only first, reconcile, then send children.
    // This ensures that when IS is sent and reconciled, AC can use the real serverId.
    //
    // ponytail: two-phase send per chunk (parents then children); ceiling = multi-level
    // deep chains might need more passes; upgrade = iterative BFS batching.

    for (let i = 0; i < sorted.length; i += BATCH_SIZE) {
      const chunk = sorted.slice(i, i + BATCH_SIZE);
      const chunkIds = new Set(chunk.map((c) => c.localId));

      // Split: roots of THIS chunk (no parent in this chunk) vs dependents.
      const chunkRoots = chunk.filter((item) => !item.parentId || !chunkIds.has(item.parentId));
      const chunkDeps = chunk.filter((item) => item.parentId && chunkIds.has(item.parentId));

      // Send roots first (filtered by errored parents from earlier batches).
      const eligibleRoots = chunkRoots.filter(
        (item) => !item.parentId || !erroredParents.has(item.parentId),
      );

      if (eligibleRoots.length > 0) {
        // Inject resolved R2 URLs into payloads AND rewrite any local parentId to the
        // parent's serverId (child whose parent synced in an earlier session — else
        // the server rejects it with a parent_id foreign-key violation).
        const rootsWithUrls = await Promise.all(
          eligibleRoots.map(async (item) => ({
            ...item,
            parentId: await resolveParentId(item.parentId),
            payload: injectBlobUrls(item.payload, blobUrlMap),
          })),
        );
        let rootResults: SyncResultItem[];
        try {
          // Backend /sync expects { items: [...] } and returns { resultados: [...] }.
          const resp = await httpPost<{ resultados: SyncResultItem[] }>("/sync", {
            items: rootsWithUrls,
          });
          rootResults = resp.resultados;
        } catch {
          for (const item of eligibleRoots) {
            await outbox.markError(item.localId, "network error");
            erroredParents.add(item.localId);
            summary.errored += 1;
          }
          rootResults = [];
        }
        for (const r of rootResults) {
          if (r.status === "error") erroredParents.add(r.localId);
        }
        await reconcile(rootResults, skippedIds, summary);
      }

      // Send dependents (children), skipping those whose parent errored.
      // Re-read each child from the outbox to pick up the rewritten parentId.
      const eligibleDeps = chunkDeps.filter((item) => !erroredParents.has(item.parentId!));

      if (eligibleDeps.length > 0) {
        // Re-read fresh outbox rows (reconcile() already rewrote parentId to serverId).
        const { getDb } = await import("../stores/db.js");
        const db = await getDb();
        const freshDeps = await Promise.all(
          eligibleDeps.map(async (item) => {
            const fresh = (await db.get("outbox", item.localId)) as
              | import("../types.js").OutboxItem
              | undefined;
            return fresh ?? item;
          }),
        );

        const depsWithUrls = await Promise.all(
          freshDeps.map(async (item) => ({
            ...item,
            parentId: await resolveParentId(item.parentId),
            payload: injectBlobUrls(item.payload, blobUrlMap),
          })),
        );
        let depResults: SyncResultItem[];
        try {
          // Backend /sync expects { items: [...] } and returns { resultados: [...] }.
          const resp = await httpPost<{ resultados: SyncResultItem[] }>("/sync", {
            items: depsWithUrls,
          });
          depResults = resp.resultados;
        } catch {
          for (const item of eligibleDeps) {
            await outbox.markError(item.localId, "network error");
            erroredParents.add(item.localId);
            summary.errored += 1;
          }
          depResults = [];
        }
        for (const r of depResults) {
          if (r.status === "error") erroredParents.add(r.localId);
        }
        await reconcile(depResults, skippedIds, summary);
      }
    }

    // Skipped = the server already has an equal-or-newer copy (last-write-wins). In
    // this app (one técnico per report) it is almost always a report conflicting with
    // ITSELF: the online finalize writes the payload directly to the server AND leaves
    // an outbox entry as an offline safety net; a focus/visibility/online-triggered
    // flush races in and re-sends that entry, which the server correctly skips.
    // reconcile() already marked the local record sincronizado and dropped the outbox
    // entry, so this is benign — no data loss, nothing for the user to act on. We log a
    // debug trace instead of a warning toast that only confused técnicos on finalize.
    if (skippedIds.length > 0) {
      console.debug(
        `[flush] ${skippedIds.length} informe(s) skipped (server copy newer/equal) — reconciled to sincronizado, no action needed.`,
      );
    }
  } finally {
    flushing = false;
  }

  // Notify flush subscribers: this was a real run (not a single-flight no-op).
  // Subscribers use this to re-read the outbox summary (e.g. SyncStatusPanel via
  // TechnicianPanel's reloadKey) so the panel count stays accurate after any
  // auto-flush (cold-start, online event, visibility change).
  for (const fn of flushSubscribers) {
    try {
      fn(summary);
    } catch {
      // Subscriber errors must never abort or corrupt the flush result.
    }
  }

  // Suppress unused-variable warning for trigger (used for future logging/metrics).
  void trigger;
  return summary;
}
